<template>
  <div class="nx-content-selector">
    <v-autocomplete
        :disabled="disabled"
        :readonly="computedReadonly"
        :item-text="itemTextKey"
        :item-value="itemValueKey"
        :items="formatItems"
        :label="label"
        :loading="loadingItem||loading"
        :multiple="multiple"
        :value="formatedItems"
        :search-input="searchText"
        @blur="onBlur"
        @click:append="openSearcher"
        @focus="onFocus"
        @input="onInput"
        @update:searchInput="fetchItems"
        :append-icon="allowSearcher ? 'search' : icon || null"
        chips
        hide-selected
        no-filter
        ref="selector"
        return-object
        v-bind="$attrs"
    >
      <template #selection="{ item, selected }">
        <v-chip
          :selected="selected"
          :text="item[itemTextKey]"
          @input="removeItem(item)"
          :close="!readonly && !disableDelete"
          class="nx-chip">
          <v-avatar>
            <v-icon class="nx-chip-icon">{{ getIcon(item) }}</v-icon>
          </v-avatar>
          <span>{{ item[itemTextKey] }}</span>
          <template v-if="allowEditItems">
            <span v-if="openModalEditor" @click.capture.stop="editModal(item)" class="cursor-pointer">
              <v-icon class="ml-2">edit</v-icon>
            </span>
            <router-link v-else :to="entityRoute(item)">
              <span class="cursor-pointer">
                <v-icon class="ml-2">edit</v-icon>
              </span>
            </router-link>
          </template>
        </v-chip>

      </template>

      <template #item="{ item }">
        <v-list-tile-avatar>
          <v-icon color="info" v-html="getIcon(item)"></v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title
              v-html="$options.filters.highlight(item[itemTextKey], searchText)"></v-list-tile-title>
        </v-list-tile-content>
      </template>
      <template slot="no-data">
        <li>
          <v-list-tile>
            <v-list-tile-avatar>
              <v-progress-circular
                  indeterminate
                  v-if="loading"
                  :width="2"
                  size="28"
              ></v-progress-circular>
              <v-icon v-else-if="searchText">not_interested</v-icon>
              <v-icon v-else>edit</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title v-if="loading">{{ $t('message.common.loading') }}
              </v-list-tile-title>
              <v-list-tile-title v-else-if="searchText">{{ $t('message.search.noDataFound') }}
              </v-list-tile-title>
              <v-list-tile-title v-else>{{ $t('message.search.typeToSearch') }}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </li>
      </template>
      <template slot="append-outer" v-if="wizardComponent">
        <v-tooltip top class="btn btn--icon">
          <v-btn icon small @click="openWizard" slot="activator">
            <v-icon color="primary">add</v-icon>
          </v-btn>
          <span
              v-html="$t('message.wizard.new.' + wizardConfig.name)">
          </span>
        </v-tooltip>
      </template>
    </v-autocomplete>

    <nx-searcher-dialog
        v-if="allowSearcher && searcherModal"
        :active="searcherModalTimeout"
        :entity="entity"
        :entityTabs="false"
        :filters="filters"
        :limit="limit"
        :text="searchText"
        :value="searcherValue"
        @dismiss="openSearcher(false)"
        @input="acceptSearcherValue"
        title="Buscador"
    >
    </nx-searcher-dialog>

    <nx-dialog-confirm
        :title="$t('message.content.selector.confirmTitle')"
        :text="$t('message.content.selector.confirmText')"
        ref="confirm"
    ></nx-dialog-confirm>

    <component
        v-if="wizardComponent"
        :is="wizardComponent"
        :active="openWizardDialog"
        :entity="wizardConfig.name"
        :config="wizardConfig"
        @dismiss="openWizardDialog = false"
        @entityCreated="entityCreated"
    ></component>

    <nx-entity-modal-editor-dialog
      v-if="openModalEditor && selectedEntityToEdit"
      :active="openModalEditorDialog"
      :entity="selectedEntityToEdit"
      :type="entity"
      v-bind="selectedEntityToEdit"
      @dismiss="closeModalEdit" />
  </div>
</template>
<script>
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import Searchable from '@/modules/searcher/mixins/searchable';
import NxSearcherDialog from '@/modules/searcher/SearcherDialog';
import NxDialogConfirm from '@/components/dialogs/Confirm';
import EntityWizard from '@/modules/entity/wizard/Wizard';
import { entityEditorRoute, entityRouter } from '@/modules/entity/router';
import { toArray } from '@/utils';
import { typeToIcon as taxonomyTypeToIcon } from '@/modules/taxonomy/config';
import { typeToIcon as contentTypeToIcon } from '@/modules/content/config';
import { selectionItemMappers } from '@/modules/searcher/config';
import NxEntityModalEditorDialog from '@/modules/entity/editor/EntityModalEditorDialog';

export default {
  name: 'nx-entity-selector',
  extends: {},
  mixins: [Searchable],
  components: {
    NxEntityModalEditorDialog,
    NxSearcherDialog,
    NxDialogConfirm,
  },
  props: {
    allowEditItems: {
      type: Boolean,
      default: () => true,
    },
    loadingItem: Boolean,
    entity: {
      type: String,
      required: true,
    },
    customFilters: {
      type: Object,
    },
    validation: {
      default: null,
    },
    label: {
      type: String,
    },
    icon: {
      type: String,
    },
    itemValueKey: {
      type: String,
      default: 'id',
    },
    itemTextKey: {
      type: String,
      default: 'name',
    },
    value: {
      type: [Object, Array, Number],
    },
    wizard: {
      type: String,
    },
    searcher: {
      type: Boolean,
      default: true,
    },
    limit: {
      type: Number,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disableSearch: {
      type: Boolean,
    },
    disableDelete: {
      type: Boolean,
    },
    name: {
      type: String,
    },
    formatedItems: {
      type: [Array, Object],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    openModalEditor: {
      type: Boolean,
    },
    showWizardIfFilled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      searchOnFiltersChange: false,
      filters: this.getFilters(),
      openWizardDialog: false,
      openModalEditorDialog: false,
      searcherModalTimeout: false,
      searcherModal: false,
      selectedEntityToEdit: null,
    };
  },
  computed: {
    allowSearcher() {
      if (this.disableSearch) {
        return false;
      }
      if (!this.searcher) {
        return false;
      }
      return (!!selectionItemMappers[this.entity] || !!this.$store.getters.searcher[this.entity]);
    },
    computedReadonly() {
      return this.readonly || this.disableSearch;
    },
    searcherValue() {
      if (isEmpty(this.value)) {
        return [];
      }
      if (!this.multiple) {
        return [this.value];
      }
      return this.value;
    },
    multiple() {
      return this.limit !== 1;
    },
    formatItems() {
      // Mix items in order to draw the selected one on the component
      const items = [...toArray(this.value), ...this.items, ...toArray(this.formatedItems)];
      // Remove duplicates
      return [...new Set(items)];
    },
    wizardConfig() {
      if (!this.wizard) {
        return null;
      }
      return this.$store.getters.getWizard(this.wizard);
    },
    wizardComponent() {
      if (!this.wizardConfig) {
        return null;
      }
      if (!this.showWizardIfFilled && this.value) {
        return null;
      }
      if (!this.wizardConfig.component) {
        // If none configured, return the entity wizard by default;
        return EntityWizard;
      }
      // Return the configured wizard
      return this.wizardConfig.component;
    },
  },
  methods: {
    entityRoute(item) {
      if (this.entity === 'content') {
        return entityEditorRoute.content(item.type, item);
      }
      return entityEditorRoute.get(this.entity, item);
    },
    openSearcher(value = true) {
      if (this.disableSearch) {
        return;
      }
      value = !!value;
      this.searcherModal = value;
      setTimeout(() => {
        this.searcherModalTimeout = value;
      }, 300);
    },
    acceptSearcherValue(value) {
      const content = this.multiple ? value : value[0];
      this.$emit('input', content);
    },
    getIcon(item) {
      if (this.icon) {
        return this.icon;
      }
      const entityWizardConfig = this.$store.getters.getWizard(this.entity);
      const entityIcon = entityWizardConfig ? entityWizardConfig.icon || null : null;
      if (!item || !item.type) {
        return entityIcon;
      }
      const type = item.type;
      switch (this.entity) {
        case 'content':
          return contentTypeToIcon(type);
        case 'taxonomy':
          return taxonomyTypeToIcon(type);
        default:
          return entityIcon;
      }
    },
    getFilters() {
      const defaultFilters = {
        limit: 10,
        page: this.page,
      };

      return Object.assign(defaultFilters, this.customFilters);
    },
    onInput(value) {
      this.searchText = '';
      this.emitValue(toArray(value));
    },
    onFocus() {
      this.$emit('focus');
    },
    onBlur() {
      this.searchText = '';
      this.items = [];
      this.$emit('blur');
    },
    // eslint-disable-next-line
    fetchItems: debounce(function (text) {
      this.$emit('search', text);

      if (text && this.searchText !== text) {
        this.searchText = text;
        const filters = Object.assign({}, this.filters);
        filters.text = text;
        this.filters = filters;
      }
    }, 300),
    removeItem(item) {
      if (this.disableDelete) {
        return;
      }
      const newValue = toArray(this.value).filter(cv => cv.id !== item.id);
      this.emitValue(newValue);
    },
    formatEntity(result) {
      return result;
    },
    emitValue(value) {
      const emitionData = this.multiple ? value : (value[0] || null);
      console.log('Entity wizard emits value', this.multiple, emitionData);
      this.$emit('input', emitionData);
    },
    openWizard() {
      this.$nextTick(() => {
        this.openWizardDialog = true;
      });
    },
    editModal(entity) {
      this.selectedEntityToEdit = entity;
      this.$nextTick(() => {
        this.openModalEditorDialog = true;
      });
    },
    closeModalEdit() {
      this.openModalEditorDialog = false;
      this.$nextTick(() => {
        this.selectedEntityToEdit = null;
      });
    },
    entityCreated(entity) {
      console.log('entity created', entity);
      if (this.multiple) {
        this.onInput([...toArray(this.value), ...toArray(entity)]);
      } else {
        this.onInput(entity);
      }
    },
  },
  mounted() {
    if ((this.value === undefined || this.value === null || isEmpty(this.value))
      && this.default) {
      this.onInput(this.default);
      this.onBlur();
    }
  },
};
</script>
